import React, { useEffect, useRef, useState } from 'react';
import { LayoutGrid, Sparkles, Bookmark, Trash2, Plus, Check } from 'lucide-react';
import { T } from '../../components/em/ui';
import { startersForRole, resolveStarter } from './presets';
import { listPresets, createPreset, deletePreset } from './presetsApi';

/**
 * Presets dropdown — opened from the dashboard edit-mode toolbar. Lets the user
 * apply a curated starter, apply / delete one of their own saved presets, or
 * snapshot the current arrangement as a new personal preset.
 *
 * Props:
 *   kind            dashboard role ('admin' | 'hr' | 'member')
 *   onApply({layout, hidden, prefs})  apply a resolved snapshot to the live grid
 *   captureSnapshot()                 returns the current { layout, hidden, prefs }
 *   onClose()
 */
const PresetMenu = ({ kind, onApply, captureSnapshot, onClose }) => {
  const starters = startersForRole(kind);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setSaved(await listPresets(kind));
      setError('');
    } catch (_) {
      setError('Could not load saved presets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  useEffect(() => {
    if (showSave && nameRef.current) nameRef.current.focus();
  }, [showSave]);

  const applyStarter = (s) => {
    onApply(resolveStarter(s, kind));
    onClose();
  };

  const applySaved = (p) => {
    onApply({ layout: p.layout, hidden: p.hidden, prefs: p.prefs });
    onClose();
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const snap = captureSnapshot();
      await createPreset(kind, { name: trimmed, ...snap });
      setName('');
      setShowSave(false);
      await refresh();
    } catch (_) {
      setError('Could not save preset.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deletePreset(id);
      setSaved((prev) => prev.filter((p) => p.id !== id));
    } catch (_) {
      setError('Could not delete preset.');
    }
  };

  return (
    <div className="dash-preset-menu" role="menu">
      {/* Starters */}
      <div className="dash-preset-section">
        <Sparkles size={13} color={T.orange} />
        <span>Starter layouts</span>
      </div>
      {starters.map((s) => (
        <button
          key={s.id}
          type="button"
          className="dash-preset-item"
          onClick={() => applyStarter(s)}
        >
          <LayoutGrid size={15} color={T.fg5} />
          <span className="dash-preset-item-main">
            <span className="dash-preset-item-name">{s.name}</span>
            <span className="dash-preset-item-desc">{s.description}</span>
          </span>
        </button>
      ))}

      {/* Saved */}
      <div className="dash-preset-section" style={{ marginTop: 6 }}>
        <Bookmark size={13} color={T.orange} />
        <span>My presets</span>
      </div>
      {loading ? (
        <div className="dash-preset-muted">Loading…</div>
      ) : saved.length === 0 ? (
        <div className="dash-preset-muted">No saved presets yet.</div>
      ) : (
        saved.map((p) => (
          <div key={p.id} className="dash-preset-item dash-preset-saved">
            <button
              type="button"
              className="dash-preset-apply"
              onClick={() => applySaved(p)}
              title={`Apply ${p.name}`}
            >
              <Bookmark size={14} color={T.fg5} />
              <span className="dash-preset-item-name">{p.name}</span>
            </button>
            <button
              type="button"
              className="dash-preset-del"
              onClick={() => remove(p.id)}
              aria-label={`Delete ${p.name}`}
              title="Delete preset"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))
      )}

      {error && <div className="dash-preset-error">{error}</div>}

      {/* Save current */}
      <div className="dash-preset-foot">
        {showSave ? (
          <div className="dash-preset-saverow">
            <input
              ref={nameRef}
              type="text"
              value={name}
              maxLength={120}
              placeholder="Preset name…"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') {
                  setShowSave(false);
                  setName('');
                }
              }}
            />
            <button
              type="button"
              className="dash-preset-savebtn"
              onClick={save}
              disabled={!name.trim() || busy}
            >
              <Check size={15} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="dash-preset-savecurrent"
            onClick={() => setShowSave(true)}
          >
            <Plus size={15} /> Save current layout
          </button>
        )}
      </div>
    </div>
  );
};

export default PresetMenu;
